import { useState } from 'react';
import { whatsappPlanUrl } from '../../config/routes';
import { CheckIcon } from '../../shared/components/icons';

const PLANS = [
  {
    id: 'standard',
    name: 'Estándar',
    highlight: false,
    perks: [
      '1 cuenta de Spotify',
      'QR principal para tu local',
      'QRs de uso único por evento',
      'Filtro de géneros musicales',
    ],
    monthly: { price: 15, label: 'Estándar mensual' },
    annual: { price: 150, label: 'Estándar anual', badge: 'Ahorra S/ 30' },
  },
  {
    id: 'standard_plus',
    name: 'Estándar Plus',
    highlight: false,
    perks: [
      '2 cuentas de Spotify',
      'Todo lo del plan Estándar',
    ],
    monthly: { price: 17, label: 'Estándar Plus mensual' },
    annual: { price: 170, label: 'Estándar Plus anual', badge: 'Ahorra S/ 34' },
  },
  {
    id: 'vip',
    name: 'VIP',
    highlight: true,
    perks: [
      '2 cuentas de Spotify',
      'Todo lo del plan Estándar',
      'Tus clientes eligen solo de la playlist de Spotify que elijas',
    ],
    monthly: { price: 21, label: 'VIP mensual' },
    annual: { price: 195, label: 'VIP anual', badge: 'Ahorra S/ 57' },
  },
];

export function PricingCards({ planIds } = {}) {
  const [period, setPeriod] = useState('monthly');
  const plans = planIds ? PLANS.filter((p) => planIds.includes(p.id)) : PLANS;
  const gridClass =
    plans.length === 1 ? 'max-w-sm mx-auto' :
    plans.length === 2 ? 'sm:grid-cols-2 max-w-2xl mx-auto' :
    'lg:grid-cols-3';

  return (
    <div className="flex w-full flex-col items-center gap-8 font-inter">
      <div className="inline-flex rounded-full border border-lounge-line bg-lounge-chassis p-1">
        {[
          { key: 'monthly', label: 'Mensual' },
          { key: 'annual', label: 'Anual' },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            aria-pressed={period === opt.key}
            onClick={() => setPeriod(opt.key)}
            className={
              'min-h-[40px] rounded-full px-5 font-mono text-[11px] uppercase tracking-[0.15em] transition ' +
              (period === opt.key
                ? 'bg-lounge-brass text-lounge-base'
                : 'text-lounge-stone hover:text-lounge-text')
            }
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className={`grid w-full gap-4 ${gridClass}`}>
        {plans.map((plan) => {
          const { price, label, badge } = plan[period];
          return (
            <div
              key={plan.id}
              className={
                'relative flex flex-col overflow-hidden rounded-lg border p-6 ' +
                (plan.highlight
                  ? 'border-lounge-brass/30 bg-lounge-raised shadow-sleeve'
                  : 'border-lounge-line bg-lounge-card')
              }
            >
              {plan.highlight && <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-lounge-brass" />}

              <div className="flex items-start justify-between gap-3">
                <h3 className="m-0 font-serif text-xl font-medium text-lounge-text">{plan.name}</h3>
                {badge && (
                  <span className="shrink-0 rounded-sm bg-lounge-brass/15 px-2 py-0.5 font-mono text-[10px]
                                   uppercase tracking-wider text-lounge-brass">
                    {badge}
                  </span>
                )}
              </div>

              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-mono text-xs text-lounge-brass">S/</span>
                <span className={`font-serif text-5xl font-semibold leading-none tabular-nums
                                  ${plan.highlight ? 'text-lounge-brass' : 'text-lounge-text'}`}>
                  {price}
                </span>
                <span className="ml-1 text-sm text-lounge-stone">{period === 'monthly' ? '/mes' : '/año'}</span>
              </div>

              <ul className="mt-6 flex flex-1 flex-col gap-2.5 border-t border-lounge-line pt-5">
                {plan.perks.map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-sm text-lounge-stone">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-lounge-brass" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>

              <a
                href={whatsappPlanUrl(label)}
                target="_blank"
                rel="noopener noreferrer"
                className={
                  'mt-8 inline-flex h-12 items-center justify-center gap-2 rounded text-sm font-semibold transition active:scale-[0.98] ' +
                  (plan.highlight
                    ? 'bg-lounge-brass text-lounge-base hover:bg-amber hover:shadow-brass'
                    : 'border border-lounge-line bg-lounge-chassis text-lounge-text hover:border-white/20')
                }
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-1.043zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
                </svg>
                Elegir {plan.name}
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
}
